import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { combineLatest, defer, type Observable, switchMap, map, of } from 'rxjs';
import { Company } from '../db/company.js';
import { MatchingWeekPermissions, MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication, JobApplicationStatus } from '../db/job-application.js';
import { JobPosition } from '../db/job-position.js';
import { MatchingScore } from '../db/MatchingScore.js';
import { MatchingScoreCache } from '../db/MatchingScoreCache.js';
import { MatchingWeekSettings } from '../db/settings.js';
import { CandidateApplicationSubmittedMail } from '../mails/candidate-mails.js';
import { formatTimeSlot } from '../mails/mail-templates.js';
import {
  CandidateAcceptedMail,
  CandidateRejectedMail,
  CandidateWaitingListMail,
  CompanyRejectedByUserMail,
} from '../mails/status-change-mails.js';
import { assertCallerIsAdmin } from './admin-guard.js';
import { getCompanyUsers, releaseInterviewSlot } from './booking.js';
import { getOrCalculateMatchingScore } from './matching-score.js';
import { getJobPositionByID } from './job-position.js';

type MatchingWeekUser = User & { hasPermission(permission: MatchingWeekPermissions, context?: string): boolean };

export const APPLY_INACTIVE_COMPANY_ERROR = 'Cannot apply to inactive companies';
export const APPLICATION_PHASE_CLOSED_ERROR = 'Application phase is closed';

export const countJobApplications = registerQuery({
  operationName: 'countJobApplications',
  type: () => Int,
  resolve: () => async () => {
    await assertCallerIsAdmin();
    return (await scopedCollection(JobApplication)).countDocuments();
  },
});

export const getJobApplicationCountByPosition = registerQuery({
  operationName: 'getJobApplicationCountByPosition',
  type: () => Int,
  params: [{ name: 'jobPositionId', type: () => ID }],
  resolve: (jobPositionId: string) => async () => {
    if (!context.serverContext) {
      const user = (await context.user()) as Maybe<MatchingWeekUser>;
      if (!user) throw new NotLoggedInError();
      const jobPosition = await getJobPositionByID(jobPositionId)({ _id: 1, companyID: 1 });
      if (!jobPosition?.companyID) throw new Error('Job position not found');
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)) {
        throw new Error('Not authorized for this job position');
      }
    }
    return (await scopedCollection(JobApplication)).countDocuments({ jobPositionId });
  },
});

export const getJobApplicationsByPosition = registerQuery({
  operationName: 'getJobApplicationsByPosition',
  type: () => [JobApplication],
  params: [{ name: 'jobPositionId', type: () => ID }],
  resolve: (jobPositionId: string) => async (gqlFields: BaseQLSelectionSet<JobApplication>) => {
    if (!context.serverContext) {
      const user = (await context.user()) as Maybe<MatchingWeekUser>;
      if (!user) throw new NotLoggedInError();
      const jobPosition = await getJobPositionByID(jobPositionId)({ _id: 1, companyID: 1 });
      if (!jobPosition?.companyID) throw new Error('Job position not found');
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)) {
        throw new Error('Not authorized for this job position');
      }
    }
    const collection = await scopedCollection(JobApplication);
    return (await collection.find({ jobPositionId }).toArray()) as JobApplication[];
  },
});

export const getJobApplicationByCurrentUserAndPosition = registerQuery({
  operationName: 'getJobApplicationByCurrentUserAndPosition',
  type: () => JobApplication,
  params: [
    { name: 'jobPositionId', type: () => ID },
    { name: 'state', type: () => JobApplicationStatus },
  ],
  resolve:
    (jobPositionId: string, state: JobApplicationStatus) => async (gqlFields: BaseQLSelectionSet<JobApplication>) => {
      if (context.serverContext) throw new Error('User not authenticated');
      if (!context.userID) throw new NotLoggedInError();
      const collection = await scopedCollection(JobApplication);
      logger.info({ query: { jobPositionId, userId: context.userID, status: state } });
      return (await collection.findOne({ jobPositionId, userId: context.userID, status: state })) as JobApplication;
    },
});

export const postJobApplications = registerMutation({
  operationName: 'postJobApplications',
  type: () => Boolean,
  resolve: (jobIDs: string[]) => async () => {
    if (!jobIDs.length) throw new Error('Cannot post job applications without job IDs');
    if (context.serverContext) throw new Error('Cannot post job applications with server context');
    if (!context.userID) throw new NotLoggedInError();

    const settingsCollection = mongo().collection(MatchingWeekSettings._collectionName);
    const settings = await settingsCollection.findOne({}, { projection: { applicationPhaseClosed: 1 } });
    if (settings?.applicationPhaseClosed !== false) throw new ExpectedError(APPLICATION_PHASE_CLOSED_ERROR);

    // Check for existing applications for the same job positions from this user
    const collection = await scopedCollection(JobApplication);
    const existingApplications = await collection
      .find({ userId: context.userID, jobPositionId: { $in: jobIDs } })
      .toArray();

    if (existingApplications.length > 0) {
      const duplicateJobIds = existingApplications.map(app => app.jobPositionId);
      throw new Error(
        `You have already applied to ${duplicateJobIds.length > 1 ? 'some of these positions' : 'this position'}`,
      );
    }

    // Block applications to jobs whose parent company has been archived (isActive === false).
    const jobCollection = await scopedCollection(JobPosition);
    const jobs = await jobCollection
      .find({ _id: { $in: jobIDs as any[] } })
      .project({ companyID: 1 })
      .toArray();
    const companyIDs = jobs.map(j => (j as any).companyID).filter(Boolean);
    if (companyIDs.length > 0) {
      const companyCollection = mongo().collection(Company._collectionName);
      const inactive = await companyCollection
        .find({ _id: { $in: companyIDs as any[] }, isActive: false })
        .project({ _id: 1 })
        .toArray();
      if (inactive.length > 0) throw new ExpectedError(APPLY_INACTIVE_COMPANY_ERROR);
    }

    for (const jobID of jobIDs) {
      const matchingScore = await getOrCalculateMatchingScore(
        context.userID,
        jobID,
      )(selectionSet(() => MatchingScore, 2));
      const jobApplication = new JobApplication({
        jobPositionId: jobID,
        userId: context.userID,
        status: JobApplicationStatus.PENDING,
        matchingScore,
      });
      await runInServerContext(() => jobApplication.create());
    }

    // Send confirmation email to the candidate
    try {
      const _mailer = await mailer;
      if (_mailer) {
        const user = await getUserByID(context.userID)({ email: 1, firstName: 1, lastName: 1, username: 1 });
        if (user?.email) {
          const jobs = await Promise.all(jobIDs.map(id => getJobPositionByID(id)({ title: 1, company: { name: 1 } })));
          const jobTitles = jobs.map(j => j?.title ?? '---').join(', ');
          const companyNames = [...new Set(jobs.map(j => j?.company?.name ?? '---'))].join(', ');
          await CandidateApplicationSubmittedMail(user, jobTitles, companyNames).send({
            mailer: _mailer,
            recipients: [new Recipient(user.email)],
          });
        }
      }
    } catch (e) {
      console.error('Failed to send application confirmation email', e);
    }

    return true;
  },
  params: [{ name: 'jobIDs', type: () => [ID] }],
});

export const subscribeJobApplicationsByUser = registerSubscription({
  operationName: 'getJobApplicationsByUser',
  type: () => [JobApplication],
  resolve:
    (userID: string) =>
    <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) => {
      return defer(async () => {
        if (context.serverContext) return;
        const user = (await context.user()) as Maybe<MatchingWeekUser>;
        if (!user) throw new NotLoggedInError();
        const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
        if (!isAdmin && userID !== user._id) throw new Error('Not authorized to view another user applications');
      }).pipe(switchMap(() => watchQuery(JobApplication._collectionName, { userId: userID }))) as Observable<
        NarrowedEntity<JobApplication, SelSet>[]
      >;
    },
  params: [{ name: 'userID', type: () => ID }],
});

export const getUserBookedApplications = registerQuery({
  operationName: 'getUserBookedApplications',
  type: () => [JobApplication],
  resolve: () => async (gqlFields: BaseQLSelectionSet<JobApplication>) => {
    if (context.serverContext) throw new Error('User not authenticated');
    if (!context.userID) throw new NotLoggedInError();
    const collection = await scopedCollection(JobApplication);
    return (await collection
      .find({ userId: context.userID, status: JobApplicationStatus.BOOKED })
      .toArray()) as JobApplication[];
  },
});

export const getConversationList = registerQuery({
  operationName: 'getConversationList',
  type: () => [JobApplication],
  resolve: (jobPositionId: string) => async (gqlFields: BaseQLSelectionSet<JobApplication>) => {
    if (context.serverContext) throw new Error('Not authenticated');
    // The print flow runs in a puppeteer browser that inherits the caller's
    // sessionID (see @adornis/print's runInCluster), so context.userID is set
    // for both direct API calls and the PDF render.
    const user = (await context.user()) as Maybe<MatchingWeekUser>;
    if (!user) throw new NotLoggedInError();

    const jobPosition = await getJobPositionByID(jobPositionId)({ _id: 1, companyID: 1 });
    if (!jobPosition?.companyID) throw new Error(`Job position "${jobPositionId}" not found`);

    if (!user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)) {
      throw new Error('Not authorized to view conversations for this job position');
    }

    const collection = await scopedCollection(JobApplication);
    return (await collection
      .find({ jobPositionId, status: JobApplicationStatus.BOOKED })
      .toArray()) as JobApplication[];
  },
  params: [{ name: 'jobPositionId', type: () => ID }],
});

/**
 * Apply a status change to a single job application: persists the new status,
 * clears interview times when leaving BOOKED, and fires the appropriate
 * candidate/company notification mails. Shared by the single and bulk
 * mutations.
 */
const applyJobApplicationStatus = async (jobApplicationID: string, status: JobApplicationStatus) => {
  //* Data loading
  const jobApplication = await getJobApplicationByID(jobApplicationID)({
    _id: 1,
    status: 1,
    user: selectionSet(() => User, 1),
    userId: 1,
    jobPositionId: 1,
    interviewStart: 1,
    interviewEnd: 1,
    interviewAttended: 1,
  });
  if (!jobApplication) throw new Error('Job application not found');

  const jobPosition = await getJobPositionByID(jobApplication.jobPositionId)({ _id: 1, title: 1, companyID: 1 });
  if (!jobPosition)
    throw new Error(
      `Job position "${jobApplication.jobPositionId}" not found for user "${context.serverContext ? undefined : context.userID}"`,
    );

  if (!context.serverContext) {
    const user = (await context.user()) as Maybe<MatchingWeekUser>;
    if (!user) throw new NotLoggedInError();
    const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
    const isOwnWithdrawal = jobApplication.userId === user._id && status === JobApplicationStatus.REJECTED_BY_USER;
    const canManageCompany =
      !!jobPosition.companyID && user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID);
    if (!isAdmin && !isOwnWithdrawal && !canManageCompany) {
      throw new Error('Not authorized to update this job application');
    }
  }

  // Silently no-op when already in the target status — bulk callers rely on this.
  if (jobApplication.status === status) return true;

  const _mailer = await mailer;
  if (!_mailer) throw new Error('Mailer not imported correctly');
  const company = await mongo()
    .collection<Pick<EntityData<Company>, '_id' | '_class' | 'name'>>(Company._collectionName)
    .findOne({ _id: jobPosition?.companyID! }, { projection: { _class: 1, name: 1 } });
  if (!company) {
    logger.error(
      { jobApplicationID: jobApplication._id, jobPositionID: jobPosition._id },
      `Did not find Company for jobPosition in status update`,
    );
    throw new Error('Could not find company for job position');
  }

  const prev = { start: jobApplication.interviewStart, end: jobApplication.interviewEnd };
  const leavingBooked = jobApplication.status === JobApplicationStatus.BOOKED && status !== JobApplicationStatus.BOOKED;

  if (
    jobApplication.status !== JobApplicationStatus.ACCEPTED &&
    jobApplication.status !== JobApplicationStatus.BOOKED &&
    status === JobApplicationStatus.ACCEPTED
  ) {
    const numberOfFreeSlotsRemaining = await getNumberOfFreeSlots(jobApplication.jobPositionId!);
    if (numberOfFreeSlotsRemaining <= 0) throw new Error('No free slots remaining');
  }

  if (leavingBooked) {
    const result = await mongo()
      .collection<JobApplication>(JobApplication._collectionName)
      .updateOne(
        { _id: jobApplication._id, status: JobApplicationStatus.BOOKED },
        { $set: { status, interviewStart: null, interviewEnd: null, interviewAttended: null, updatedAt: new Date() } },
      );
    // Another status update won the race; do not release or send its mails again.
    if (result.matchedCount !== 1) return true;
    if (prev.start && prev.end) await releaseInterviewSlot(jobApplication.jobPositionId!, prev.start, prev.end);
  } else {
    jobApplication.status = status;
    await runInServerContext(() => jobApplication.save());
  }

  //* Send mails
  logger.info(
    { jobApplicationID: jobApplication._id, jobPositionID: jobPosition._id, status },
    'Sending status change mail',
  );
  const userVar = jobApplication.user!;
  const jobPositionTitle = jobPosition.title ?? '-';
  const bookAppointmentLink = A.absoluteUrl('/book-appointment/' + jobApplication.jobPositionId);
  const applicantAsRecipient = new Recipient(jobApplication.user!.email);

  switch (status) {
    case JobApplicationStatus.ACCEPTED:
      await CandidateAcceptedMail(userVar, jobPositionTitle, bookAppointmentLink, company).send({
        mailer: _mailer,
        recipients: applicantAsRecipient,
      });
      break;
    case JobApplicationStatus.WAITING:
      await CandidateWaitingListMail(userVar, jobPositionTitle, company).send({
        mailer: _mailer,
        recipients: applicantAsRecipient,
      });
      break;
    case JobApplicationStatus.REJECTED:
      await CandidateRejectedMail(userVar, jobPositionTitle, company).send({
        mailer: _mailer,
        recipients: applicantAsRecipient,
      });
      break;
    case JobApplicationStatus.REJECTED_BY_USER: {
      const companyUsers = await getCompanyUsers(jobPosition?.companyID!); //TODO: refactor this architecture wise. it's imported from booking.ts...
      const companyUsersWithEmail = companyUsers.filter(u => u.email);
      if (!prev.start || !prev.end) break; // do nothing if not booked yet
      const slotText = formatTimeSlot(prev.start, prev.end);
      for (const companyUser of companyUsersWithEmail) {
        await CompanyRejectedByUserMail(companyUser, jobPositionTitle, userVar, slotText, company).send({
          mailer: _mailer,
          recipients: new Recipient(companyUser.email),
        });
      }
      break;
    }
  }
  return true;
};

/**
 * Company (or admin) confirms whether a booked interview actually took place.
 * `attended === null` resets it to "not yet confirmed". Only a user managing the
 * job's company — or an admin — may record the outcome.
 */
export const confirmInterviewAttendance = registerMutation({
  operationName: 'confirmInterviewAttendance',
  type: () => Boolean,
  params: [
    { name: 'jobApplicationID', type: () => ID },
    { name: 'attended', type: () => Boolean },
  ],
  resolve: (jobApplicationID: string, attended: Maybe<boolean>) => async () => {
    if (context.serverContext) throw new Error('Not authenticated');
    const user = (await context.user()) as Maybe<MatchingWeekUser>;
    if (!user) throw new NotLoggedInError();

    const jobApplication = await getJobApplicationByID(jobApplicationID)({
      _id: 1,
      status: 1,
      jobPositionId: 1,
      interviewAttended: 1,
    });
    if (!jobApplication) throw new Error('Job application not found');

    const jobPosition = await getJobPositionByID(jobApplication.jobPositionId)({ _id: 1, companyID: 1 });
    if (!jobPosition?.companyID) throw new Error('Job position not found');

    const isAdmin = user.roles?.some(r => r.name === MatchingWeekRole.ADMIN) ?? false;
    if (!isAdmin && !user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)) {
      throw new Error('Not authorized to confirm interviews for this job position');
    }

    // Only booked interviews can have taken place.
    if (jobApplication.status !== JobApplicationStatus.BOOKED) {
      throw new Error('Only booked interviews can be confirmed');
    }

    jobApplication.interviewAttended = attended ?? null;
    await runInServerContext(() => jobApplication.save());
    return true;
  },
});

export const updateJobApplicationStatus = registerMutation({
  operationName: 'updateJobApplicationStatus',
  type: () => Boolean,
  params: [
    { name: 'jobApplicationID', type: () => ID },
    { name: 'status', type: () => JobApplicationStatus },
  ],
  resolve: (jobApplicationID: string, status: JobApplicationStatus) => async () =>
    applyJobApplicationStatus(jobApplicationID, status),
});

/**
 * Bulk status change. Each item is processed independently; per-item failures
 * are logged and the loop continues. The mutation returns the number of items
 * that failed so the caller can surface a partial-success banner if non-zero.
 */
export const updateJobApplicationStatuses = registerMutation({
  operationName: 'updateJobApplicationStatuses',
  type: () => Int,
  params: [
    { name: 'jobApplicationIDs', type: () => [ID] },
    { name: 'status', type: () => JobApplicationStatus },
  ],
  resolve: (jobApplicationIDs: string[], status: JobApplicationStatus) => async () => {
    if (!jobApplicationIDs.length) return 0;
    let failures = 0;
    for (const id of jobApplicationIDs) {
      try {
        await applyJobApplicationStatus(id, status);
      } catch (err) {
        logger.error({ id, status, err }, 'Bulk status change: skipping failed application');
        failures += 1;
      }
    }
    return failures;
  },
});

export const subscribeJobApplicationsWithFilter = registerSubscription({
  operationName: 'subscribeJobApplicationsWithFilter',
  type: () => [JobApplication],
  resolve: (skip: number, limit: number, filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<JobApplication>) => {
    return defer(async () => {
      if (context.serverContext) return filter.getMongoFilter();
      const user = (await context.user()) as Maybe<MatchingWeekUser>;
      if (!user) throw new NotLoggedInError();
      const isAdmin = user.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (isAdmin) return filter.getMongoFilter();

      const companyCollection = mongo().collection<EntityData<Company>>(Company._collectionName);
      const companies = await companyCollection.find({ userIDs: user._id }, { projection: { _id: 1 } }).toArray();
      const companyIDs = companies
        .map(company => company._id)
        .filter(companyID => user.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, companyID));
      if (!companyIDs.length) throw new Error('Not authorized to view job applications');
      const jobPositionCollection = mongo().collection<EntityData<JobPosition>>(JobPosition._collectionName);
      const jobPositions = await jobPositionCollection
        .find({ companyID: { $in: companyIDs } }, { projection: { _id: 1 } })
        .toArray();
      return {
        $and: [filter.getMongoFilter(), { jobPositionId: { $in: jobPositions.map(jobPosition => jobPosition._id) } }],
      };
    }).pipe(
      switchMap(scopedFilter =>
        subscribePaginatedAggregation<typeof JobApplication>(
          JobApplication,
          skip,
          limit,
          scopedFilter,
          [
            { $lookup: { from: AdornisUser._collectionName, localField: 'userId', foreignField: '_id', as: 'user' } },
            { $unwind: { path: '$user', preserveNullAndEmptyArrays: true } },
            {
              $lookup: {
                from: MatchingScoreCache._collectionName,
                let: { uid: '$userId', jid: '$jobPositionId' },
                pipeline: [
                  {
                    $match: { $expr: { $and: [{ $eq: ['$userID', '$$uid'] }, { $eq: ['$jobPositionID', '$$jid'] }] } },
                  },
                  { $sort: { updatedAt: -1 } },
                  { $limit: 1 },
                ],
                as: 'matchingScore',
              },
            },
            { $unwind: { path: '$matchingScore', preserveNullAndEmptyArrays: true } },
            { $addFields: { matchingScore: '$matchingScore.matchingScore' } },
            { $sort: filter.getMongoOptions().sort },
          ],
          {},
        ),
      ),
    );
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

// BOOKED applications consume their interview slot directly (via the booked
// time slot itself), so only ACCEPTED is counted here.
const getNumberOfFreeSlots = async (jobPositionID: string): Promise<number> => {
  const jobPosition = await getJobPositionByID(jobPositionID)({ _id: 1, availableSlots: 1 });
  if (!jobPosition) throw new Error(`Job position "${jobPositionID}" not found!`);
  // This count is deliberately public on the job detail and booking pages. Use the
  // raw collection so the caller's private JobApplication publish filter does not turn it into zero.
  const collection = mongo().collection(JobApplication._collectionName);
  const count = await collection.countDocuments({
    jobPositionId: jobPositionID,
    status: JobApplicationStatus.ACCEPTED,
  });
  return jobPosition.availableSlots! - count;
};

export const subscribeNumberOfFreeSlotsAvailable = registerSubscription({
  operationName: 'subscribeNumberOfFreeSlotsAvailable',
  type: () => Int,
  resolve: (jobPositionID: string) => () =>
    combineLatest([
      watchEntireCollection(JobApplication._collectionName),
      watchQuery(JobPosition._collectionName, { _id: jobPositionID }),
    ]).pipe(switchMap(() => getNumberOfFreeSlots(jobPositionID))),
  params: [{ name: 'jobPositionID', type: () => ID }],
});

export const getJobApplicationsByIDs = registerQuery({
  operationName: 'getJobApplicationsByIDs',
  type: () => [JobApplication],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) =>
      readEntitiesByIDs(JobApplication, ids, gqlFields),
});

export const subscribeJobApplicationsByIDs = registerSubscription({
  operationName: 'subscribeJobApplicationsByIDs',
  type: () => [JobApplication],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(JobApplication, ids, gqlFields),
});

export const getAllJobApplications = registerQuery({
  operationName: 'getAllJobApplications',
  type: () => [JobApplication],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) =>
      readAllEntities(JobApplication, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllJobApplications = registerSubscription({
  operationName: 'subscribeAllJobApplications',
  type: () => [JobApplication],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) =>
      watchAllEntities(JobApplication, gqlFields, { skip, limit, excludeIDs }),
});

export const getJobApplicationByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getJobApplicationsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeJobApplicationByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<JobApplication>>(gqlFields: SelSet) =>
    id ? subscribeJobApplicationsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
